import { ReactNode, useMemo } from 'react';

import {
  PersistentCacheProvider,
  QueryCacheProvider,
  type QueryCacheSubscriptionChangeHandler,
} from '@kaiizen/react-persistent-cache';

import { useReloadWhenAnotherWindowChangesIdentity } from '../../apiState/identityAcrossWindows';
import { AuthPersistenceProvider, PreWarmCacheProvider, ScanSynchronizerProvider, SyncPersistenceProvider, useIsOffline } from '../../contexts';
import {
  createPropertyProjectSubscriptionChangeHandler,
  createScanDataSubscriptionChangeHandler,
  isPropertyProjectRealtimeQueryKey,
  type RealtimeClient,
} from '../../realtime';
import { AuthProvider, useAuth } from '../../useAuth';

import { FileCacheContextProvider, FileSystemContextProvider, InMemoryFileCache } from '../../FileSystem';
import { FileSystemPhotoGalleryProvider } from '../FileSystemPhotoGalleryProvider';
import { IndexedDbPersistenceLayer } from './IndexedDbPersistenceLayer';
import { WebInMemoryFileSystem } from './WebInMemoryFileSystem';
import { WebKaiProApiStateProvider } from './WebKaiProApiStateProvider';
import { createWebPersistenceLayers } from './webPersistenceLayers';

const WEB_FILE_CACHE_MAX_BYTES = 1024 * 1024 * 1024;

const validLogLevels = ['debug', 'info', 'none'];

const logLevelFromQueryString = typeof window !== 'undefined'
  ? (new URLSearchParams(window.location.search).get('logLevel') as 'debug' | 'info' | 'none' | undefined)
  : 'info';
const defaultLogLevel = validLogLevels.includes(logLevelFromQueryString ?? '') ? logLevelFromQueryString! : 'info';

export const KaiProWebReactContext = ({
  children,
  logLevel = defaultLogLevel,
  realtimeClient,
}: {
  children: ReactNode;
  logLevel?: 'debug' | 'info' | 'none';
  realtimeClient?: RealtimeClient;
}) => {
  useReloadWhenAnotherWindowChangesIdentity();
  const { cache: persistenceLayer, sync: syncPersistenceLayer, auth: authPersistenceLayer } = useMemo(createWebPersistenceLayers, []);
  const webFileSystem = useMemo(() => new WebInMemoryFileSystem(), []);

  return (
    <WebKaiProApiStateProvider>
      <PersistentCacheProvider persistenceLayer={persistenceLayer}>
        <AuthPersistenceProvider persistenceLayer={authPersistenceLayer}>
          <AuthProvider>
            <RealtimeAwareProviders
              logLevel={logLevel}
              realtimeClient={realtimeClient}
              syncPersistenceLayer={syncPersistenceLayer}
              webFileSystem={webFileSystem}
            >
              {children}
            </RealtimeAwareProviders>
          </AuthProvider>
        </AuthPersistenceProvider>
      </PersistentCacheProvider>
    </WebKaiProApiStateProvider>
  );
};

const RealtimeAwareProviders = ({
  children,
  logLevel,
  realtimeClient,
  syncPersistenceLayer,
  webFileSystem,
}: {
  children: ReactNode;
  logLevel: 'debug' | 'info' | 'none';
  realtimeClient?: RealtimeClient;
  syncPersistenceLayer: IndexedDbPersistenceLayer;
  webFileSystem: WebInMemoryFileSystem;
}) => {
  const { orgId, sessionId } = useAuth();
  const onSubscriptionChanges = useMemo<QueryCacheSubscriptionChangeHandler | undefined>(
    () => {
      if (!realtimeClient) return undefined;
      const scanDataSubscriptionChanges = createScanDataSubscriptionChangeHandler(realtimeClient, { currentSessionId: sessionId });
      const propertyProjectSubscriptionChanges = createPropertyProjectSubscriptionChangeHandler(realtimeClient, orgId);
      return (queryCache, key, subscriberCount) => {
        scanDataSubscriptionChanges(queryCache, key, subscriberCount);
        if (isPropertyProjectRealtimeQueryKey(key)) {
          propertyProjectSubscriptionChanges(queryCache, key, subscriberCount);
        }
      };
    },
    [orgId, realtimeClient, sessionId],
  );
  const webFileCache = useMemo(() =>
    new InMemoryFileCache({
      fileSystem: webFileSystem,
      cacheDirectory: `${webFileSystem.documentDirectory ?? '/'}file-cache/`,
      maxCacheSize: WEB_FILE_CACHE_MAX_BYTES,
    }), [webFileSystem]);

  return (
    <FileSystemContextProvider fileSystem={webFileSystem}>
      <FileCacheContextProvider fileCache={webFileCache}>
        <PreWarmCacheProvider verbose useIsOffline={useIsOffline}>
          <QueryCacheProvider key={orgId ?? 'unauthenticated'} useIsOffline={useIsOffline} onSubscriptionChanges={onSubscriptionChanges}>
            <SyncPersistenceProvider persistenceLayer={syncPersistenceLayer}>
              <FileSystemPhotoGalleryProvider FileSystem={webFileSystem} fileCache={webFileCache} realtimeClient={realtimeClient}>
                <ScanSynchronizerProvider logLevel={logLevel}>
                  {children}
                </ScanSynchronizerProvider>
              </FileSystemPhotoGalleryProvider>
            </SyncPersistenceProvider>
          </QueryCacheProvider>
        </PreWarmCacheProvider>
      </FileCacheContextProvider>
    </FileSystemContextProvider>
  );
};
